import React from 'react';
import { Spinner } from 'react-bootstrap';
import {
  FaChevronLeft, FaChevronRight, FaPlus, FaCheck,
  FaEye, FaEyeSlash,
} from 'react-icons/fa';

const MONTHS_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const DAY_FULL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const CalendarHeader = ({
  year, month, weekStart, dayView, view, onViewChange,
  onPrev, onNext, onToday, onAddEvent,
  workingDays, onWorkingDaysChange, onSaveSettings, settingsSaving, settingsDirty,
  studentSelfScheduling, onStudentSelfSchedulingChange,
  showNonWorkingDays, onToggleNonWorkingDays, isAdmin = true,
}) => {
  const now = new Date();
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth();
  const isCurrentWeek = (() => {
    if (!weekStart) return false;
    const t = new Date(); t.setHours(0, 0, 0, 0);
    const ws = new Date(weekStart); ws.setHours(0, 0, 0, 0);
    const diff = t.getTime() - ws.getTime();
    return diff >= 0 && diff < 7 * 86400000;
  })();
  const isToday = (() => {
    if (!dayView) return false;
    const t = new Date(); t.setHours(0, 0, 0, 0);
    const d = new Date(dayView); d.setHours(0, 0, 0, 0);
    return t.getTime() === d.getTime();
  })();
  const showTodayBtn = view === 'month' ? !isCurrentMonth : view === 'day' ? !isToday : !isCurrentWeek;

  /* ── Title formatters ── */
  const titleYear = view === 'week' && weekStart
    ? new Date(weekStart).getFullYear()
    : view === 'day' && dayView
    ? new Date(dayView).getFullYear()
    : year;

  // Desktop title (full month name)
  const desktopTitle = (() => {
    if (view === 'month') return MONTHS_FULL[month];
    if (view === 'day' && dayView) {
      const d = new Date(dayView);
      return `${MONTHS_FULL[d.getMonth()]} ${d.getDate()}`;
    }
    if (!weekStart) return '';
    const s = new Date(weekStart);
    const e = new Date(weekStart); e.setDate(e.getDate() + 6);
    if (s.getMonth() === e.getMonth()) {
      return `${MONTHS_FULL[s.getMonth()]} ${s.getDate()} – ${e.getDate()}`;
    }
    return `${MONTHS_SHORT[s.getMonth()]} ${s.getDate()} – ${MONTHS_SHORT[e.getMonth()]} ${e.getDate()}`;
  })();

  // Mobile title (compact: "26 Feb 1–7" or "Feb 26" or "Feb")
  const mobileTitle = (() => {
    if (view === 'month') return MONTHS_SHORT[month];
    if (view === 'day' && dayView) {
      const d = new Date(dayView);
      return `${MONTHS_SHORT[d.getMonth()]} ${d.getDate()}`;
    }
    if (!weekStart) return '';
    const s = new Date(weekStart);
    const e = new Date(weekStart); e.setDate(e.getDate() + 6);
    const yr = String(s.getFullYear()).slice(2);
    if (s.getMonth() === e.getMonth()) {
      return `${yr} ${MONTHS_SHORT[s.getMonth()]} ${s.getDate()}–${e.getDate()}`;
    }
    return `${MONTHS_SHORT[s.getMonth()]} ${s.getDate()}–${MONTHS_SHORT[e.getMonth()]} ${e.getDate()}`;
  })();

  const toggleDay = (dayNum) => {
    const next = workingDays.includes(dayNum)
      ? workingDays.filter((d) => d !== dayNum)
      : [...workingDays, dayNum].sort((a, b) => a - b);
    onWorkingDaysChange(next);
  };

  return (
    <div className="ic-header">
      {/* Nav arrows */}
      <div className="ic-nav-arrows">
        <button className="ic-arrow" onClick={onPrev}><FaChevronLeft size={11} /></button>
        <button className="ic-arrow" onClick={onNext}><FaChevronRight size={11} /></button>
      </div>

      {/* Title — desktop vs mobile */}
      <h2 className="ic-month-title">
        <span className="ic-title-desktop">{desktopTitle}</span>
        <span className="ic-title-mobile">{mobileTitle}</span>
        {' '}<span className="ic-year">{titleYear}</span>
      </h2>

      {showTodayBtn && (
        <button className="ic-today-chip" onClick={onToday}>Today</button>
      )}

      {/* Separator */}
      <span className="ic-header-sep" />

      {/* Working day pills — admin only */}
      {isAdmin && (
      <div className="ic-wd-inline">
        <div className="wd-pills">
          {DAY_LABELS.map((label, idx) => (
            <button
              key={idx}
              className={`wd-pill ${workingDays.includes(idx) ? 'wd-pill-on' : ''} ${idx === 0 ? 'wd-pill-sun' : ''}`}
              onClick={() => toggleDay(idx)}
              type="button"
              title={DAY_FULL[idx]}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Student self-scheduling toggle */}
        {onStudentSelfSchedulingChange && (
          <label className="ic-sss-toggle" title="Allow students to schedule their own interviews">
            <input
              type="checkbox"
              checked={!!studentSelfScheduling}
              onChange={(e) => onStudentSelfSchedulingChange(e.target.checked)}
            />
            <span className="ic-sss-label">Student scheduling</span>
          </label>
        )}

        {settingsDirty && (
          <button className="wd-save" onClick={onSaveSettings} disabled={settingsSaving}>
            {settingsSaving ? <Spinner animation="border" size="sm" /> : <><FaCheck size={10} /> Save</>}
          </button>
        )}
      </div>
      )}

      {/* Non-working days toggle (week view only) */}
      {view === 'week' && (
        <button
          className={`ic-nwd-toggle ${showNonWorkingDays ? '' : 'ic-nwd-toggle-hidden'}`}
          onClick={onToggleNonWorkingDays}
          title={showNonWorkingDays ? 'Hide non-working days' : 'Show non-working days'}
        >
          {showNonWorkingDays ? <FaEyeSlash size={11} /> : <FaEye size={11} />}
          <span className="ic-nwd-text">{showNonWorkingDays ? 'Hide' : 'Show'} off days</span>
        </button>
      )}

      {/* Spacer pushes right section */}
      <span className="ic-header-spacer" />

      {/* View toggle + New Event */}
      <div className="ic-header-right">
        <div className="ic-view-toggle">
          <button
            className={`ic-view-btn ic-view-btn-month${view === 'month' ? ' ic-view-btn-active' : ''}`}
            onClick={() => onViewChange('month')}
          >Month</button>
          <button
            className={`ic-view-btn${view === 'week' ? ' ic-view-btn-active' : ''}`}
            onClick={() => onViewChange('week')}
          >Week</button>
          <button
            className={`ic-view-btn ic-view-btn-day${view === 'day' ? ' ic-view-btn-active' : ''}`}
            onClick={() => onViewChange('day')}
          >Day</button>
        </div>
        {isAdmin && onAddEvent && (
        <button className="ic-add-btn" onClick={onAddEvent}>
          <FaPlus size={11} />
          <span className="ic-add-text">New Event</span>
        </button>
        )}
      </div>
    </div>
  );
};

export default CalendarHeader;
